import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { contactAPI } from '../utils/api';
import { useToast } from '../hooks/use-toast';

const ContactSubmissions = () => {
 const { toast } = useToast();
 const [submissions, setSubmissions] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);

 const fetchSubmissions = useCallback(async () => {
 try {
 setLoading(true);
 const data = await contactAPI.getSubmissions();
 setSubmissions(data.submissions || []);
 setError(null);
 } catch (err) {
 console.error('Error fetching submissions:', err);
 setError('Failed to load submissions');
 toast({
 title: "Error",
 description: "Failed to load contact submissions",
 variant: "destructive",
 });
 } finally {
 setLoading(false);
 }
 }, [toast]);

 useEffect(() => {
 fetchSubmissions();
 }, [fetchSubmissions]);

 const formatDate = (dateString) => {
 return new Date(dateString).toLocaleDateString('en-US', {
 year: 'numeric',
 month: 'long',
 day: 'numeric',
 hour: '2-digit',
 minute: '2-digit'
 });
 };

 if (loading) {
 return (
 <div className="admin-page">
 <div className="section-container">
 <h1 className="page-title">Contact Submissions</h1>
 <p>Loading submissions...</p>
 </div>
 </div>
 );
 }

 if (error) {
 return (
 <div className="admin-page">
 <div className="section-container">
 <h1 className="page-title">Contact Submissions</h1>
 <Card>
 <CardContent className="p-6">
 <p className="text-red-600">{error}</p>
 <Button onClick={fetchSubmissions} className="mt-4">
 Try Again
 </Button>
 </CardContent>
 </Card>
 </div>
 </div>
 );
 }

 return (
 <div className="admin-page">
 <div className="section-container">
 <div className="flex justify-between items-center mb-6">
 <h1 className="page-title">Contact Submissions</h1>
 <Button onClick={fetchSubmissions} variant="outline">
 Refresh
 </Button>
 </div>

 {submissions.length === 0 ? (
 <Card>
 <CardContent className="p-6">
 <p>No contact submissions yet.</p>
 </CardContent>
 </Card>
 ) : (
 <div className="submissions-grid">
 {submissions.map((submission) => (
 <Card key={submission.id} className="submission-card">
 <CardHeader>
 <CardTitle className="flex justify-between items-start">
 <span>{submission.parent_name}</span>
 <span className="text-sm font-normal text-gray-500">
 {formatDate(submission.submitted_at)}
 </span>
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="submission-details">
 <div className="detail-row">
 <strong>Email:</strong> 
 <a href={`mailto:${submission.email}`} className="text-blue-600 hover:underline">
 {submission.email}
 </a>
 </div>
 <div className="detail-row">
 <strong>Phone:</strong> 
 <a href={`tel:${submission.phone}`} className="text-blue-600 hover:underline">
 {submission.phone}
 </a>
 </div>
 {submission.child_age && (
 <div className="detail-row">
 <strong>Child Age:</strong> {submission.child_age}
 </div>
 )}
 {submission.message && (
 <div className="detail-row">
 <strong>Message:</strong>
 <p className="mt-1 text-gray-700">{submission.message}</p>
 </div>
 )}
 <div className="detail-row">
 <strong>Status:</strong> 
 <span className={`status-badge ${submission.status}`}>
 {submission.status}
 </span>
 </div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 )}
 </div>

 <style jsx>{`
 .admin-page {
 padding: 2rem 0;
 min-height: 100vh;
 background-color: #f8fafc;
 }

 .submissions-grid {
 display: grid;
 gap: 1.5rem;
 grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
 }

 .submission-card {
 background: white;
 border: 1px solid #e2e8f0;
 border-radius: 8px;
 box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
 }

 .submission-details {
 display: flex;
 flex-direction: column;
 gap: 0.75rem;
 }

 .detail-row {
 display: flex;
 flex-direction: column;
 gap: 0.25rem;
 }

 .detail-row strong {
 font-weight: 600;
 color: #374151;
 }

 .status-badge {
 display: inline-block;
 padding: 0.25rem 0.5rem;
 border-radius: 4px;
 font-size: 0.75rem;
 font-weight: 500;
 text-transform: uppercase;
 }

 .status-badge.new {
 background-color: #dbeafe;
 color: #1e40af;
 }

 .status-badge.contacted {
 background-color: #d1fae5;
 color: #065f46;
 }

 .status-badge.closed {
 background-color: #f3f4f6;
 color: #374151;
 }
 `}</style>
 </div>
 );
};

export default ContactSubmissions;